import React, { useMemo } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip as BsTooltip } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useHistory } from "react-router-dom";
import {
  DateGapsResponse,
  DateGapStatement,
  DuplicateBankTransactionGroup,
  DuplicateBankTransactionsResponse,
  getDuplicateBankTransactions,
  getDuplicateBankTransactionsForProject,
  getJobTransactionsDateGaps,
  getJobTransactionsDateGapsTagLevel,
} from "../../../../docuclipper/api";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AgTable from "../AnalyzeTransactionsTable/AgTable";
import { getDocumentName } from "../AnalyzeTransactionsTable/utils";
import moment from "moment";
import { DateRangeFloatingFilter } from "src/views/Docuclipper/AgGrid/DateRangeFloatingFilter";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import {
  CELL_BORDER,
  CELL_COLORS,
  CELL_LEGEND,
  buildCoverageCsv,
  computeCellInfo,
  groupStatementsByAccount,
  monthRange,
  stateLabel,
} from "./coverage";

// Coverage/reconciliation view for accountants and forensic accountants.
// The question this screen must answer in under 5 seconds:
//   "Do I have every statement for every account for every month in this
//    engagement — and is each one reconciled?"
//
// Design decisions:
//   - Rows = accounts, columns = months → scannable grid, not a scatter plot.
//   - Cell state is tri-level (missing / covered-unreconciled / covered-
//     reconciled) with a fourth "transactions-only" state when we see TLs
//     but no statement metadata (indicates a partial import).
//   - Summary chips at top give a quick pass/fail without reading the grid.
//   - Detailed statement table below is the audit trail — every statement
//     shown with its start/end dates, balances, delta, reconciliation status.
//
// Pure helpers (cell state, CSV, month range) live in ./coverage.ts so they
// can be unit-tested without mounting the Redux-connected component.

type Props = {};
export const DateGaps: React.FC<Props> = () => {
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const history = useHistory();
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  // Document list for the current job, so we can render each period's source
  // document name. `documentId` is already on every DateGapStatement — this
  // just resolves it to the original filename. Coerce to string: the payload's
  // documentId is a number but JobDocument.documentId is a string (getDocumentName
  // compares with ===, so an un-coerced number silently misses the lookup).
  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );
  const docName = React.useCallback(
    (documentId: number) => getDocumentName(jobDocs || [], String(documentId)),
    [jobDocs]
  );

  // Route to the bank-statement upload flow. Preserves the current l1/l2
  // query params (if present) so the upload lands back in the same project
  // the auditor is working in. The upload UI doesn't accept an account or
  // month hint — it infers those from the uploaded PDF — so we just open
  // the form and let the user drop the file.
  const goToUpload = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    // Strip analyze-specific params that don't belong on the upload page.
    ["mode", "analyzeMode", "docType"].forEach((k) => params.delete(k));
    const qs = params.toString();
    history.push(`/extract/bankMode/${qs ? "?" + qs : ""}`);
  }, [history]);

  const [data, setData] = React.useState<DateGapsResponse>({
    dateGaps: {},
    statements: [],
    summary: {
      totalMonths: 0,
      coveredMonths: 0,
      missingMonths: 0,
      totalStatements: 0,
      reconciledStatements: 0,
      unreconciledStatements: 0,
      perAccount: [],
    },
  });

  const jobId = job ? job.id : "";

  // Duplicate bank-transactions data — same backend as the transactions-page
  // chip. Fetches per-job when jobId is present; otherwise falls back to
  // the project-scope variant when l1+l2 are available. Both paths share
  // the same response shape, the same Overlapping-periods section, and
  // the same silent-degradation behavior.
  const [duplicates, setDuplicates] =
    React.useState<DuplicateBankTransactionsResponse | null>(null);
  const [duplicatesErrored, setDuplicatesErrored] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    let p: Promise<DuplicateBankTransactionsResponse> | null = null;
    if (jobId) {
      p = getDuplicateBankTransactions(jobId);
    } else if (l1 && l2) {
      p = getDuplicateBankTransactionsForProject(l1, l2);
    } else {
      setDuplicates(null);
      setDuplicatesErrored(false);
      return;
    }
    p
      .then((rsp) => {
        if (!cancelled) {
          setDuplicates(rsp);
          setDuplicatesErrored(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDuplicates(null);
          setDuplicatesErrored(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, l1, l2]);

  const getGaps = React.useCallback(() => {
    const fn = jobId
      ? getJobTransactionsDateGaps(jobId, startDate, endDate)
      : getJobTransactionsDateGapsTagLevel(l1, l2, startDate, endDate);
    fn.then((rsp) => setData(rsp)).catch(() => null);
  }, [jobId, l1, l2, startDate, endDate]);

  React.useEffect(() => {
    getGaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, l1, l2]);

  // Auto-populate the date pickers from the discovered range on first load so
  // the grid isn't empty on arrival.
  React.useEffect(() => {
    const { dateGaps, statements } = data;
    if (startDate || endDate) return;
    const months = new Set<string>();
    Object.keys(dateGaps).forEach((a) =>
      Object.keys(dateGaps[a]).forEach((m) => months.add(m))
    );
    statements.forEach((s) => {
      if (s.startDate) months.add(String(s.startDate).slice(0, 7));
      if (s.endDate) months.add(String(s.endDate).slice(0, 7));
    });
    if (months.size === 0) return;
    const sorted = Array.from(months).sort();
    setStartDate(sorted[0] + "-01");
    setEndDate(sorted[sorted.length - 1] + "-01");
  }, [data, startDate, endDate]);

  const accounts: string[] = useMemo(() => {
    const set = new Set<string>();
    Object.keys(data.dateGaps).forEach((a) => set.add(a));
    data.statements.forEach((s) => {
      if (s.accountName) set.add(s.accountName);
    });
    return Array.from(set).filter(Boolean).sort();
  }, [data]);

  const months: string[] = useMemo(() => {
    if (startDate && endDate) {
      return monthRange(startDate.slice(0, 7), endDate.slice(0, 7));
    }
    const set = new Set<string>();
    Object.keys(data.dateGaps).forEach((a) =>
      Object.keys(data.dateGaps[a]).forEach((m) => set.add(m))
    );
    data.statements.forEach((s) => {
      if (s.startDate) set.add(String(s.startDate).slice(0, 7));
      if (s.endDate) set.add(String(s.endDate).slice(0, 7));
    });
    return Array.from(set).sort();
  }, [data, startDate, endDate]);

  // For each (account, month) decide the cell state. A statement covers any
  // month its [startDate, endDate] span touches; TL presence is taken from
  // the legacy `dateGaps` map.
  const statementsByAccount = useMemo(
    () => groupStatementsByAccount(data.statements),
    [data.statements]
  );

  const cellInfo = React.useCallback(
    (account: string, month: string) =>
      computeCellInfo(account, month, data.dateGaps, statementsByAccount),
    [data.dateGaps, statementsByAccount]
  );

  const summary = data.summary;

  // Serialize the coverage grid to CSV for paste-into-workpapers. Logic is in
  // coverage.ts; this wrapper handles the browser download side effect.
  const exportCoverageCsv = React.useCallback(() => {
    if (accounts.length === 0 || months.length === 0) return;
    const csv = buildCoverageCsv(
      accounts,
      months,
      data.dateGaps,
      statementsByAccount,
      summary.perAccount
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coverage-${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, months, summary, data, statementsByAccount]);

  const gapMonths = useMemo(() => {
    const out: Array<{ account: string; month: string }> = [];
    accounts.forEach((a) =>
      months.forEach((m) => {
        if (cellInfo(a, m).state === "missing") out.push({ account: a, month: m });
      })
    );
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, months, data]);

  // Statement detail rows for the audit table
  const statementRows = useMemo(() => {
    return data.statements.map((s) => ({
      account: s.accountName,
      documentName: docName(s.documentId),
      chunk: s.chunk,
      startDate: s.startDate ? String(s.startDate).slice(0, 10) : "",
      endDate: s.endDate ? String(s.endDate).slice(0, 10) : "",
      startBalance: s.startBalance,
      endBalance: s.endBalance,
      expectedEndBalance: s.expectedEndBalance,
      delta: s.delta,
      txCount: s.txCount,
      reconciliationStatus: s.reconciliationStatus,
    }));
  }, [data.statements, docName]);

  const statementColumnDefs = [
    { headerName: "Account", field: "account", filter: "agSetColumnFilter", pinned: "left" },
    { headerName: "Document", field: "documentName", filter: "agSetColumnFilter", minWidth: 200 },
    { headerName: "Chunk", field: "chunk", width: 80 },
    { headerName: "Period start", field: "startDate", filter: "agDateColumnFilter", filterParams: datefilterParamsYYYYMMDD, floatingFilterComponent: DateRangeFloatingFilter, floatingFilterComponentParams: { suppressFilterButton: true }, minWidth: 200 },
    { headerName: "Period end", field: "endDate", filter: "agDateColumnFilter", filterParams: datefilterParamsYYYYMMDD, floatingFilterComponent: DateRangeFloatingFilter, floatingFilterComponentParams: { suppressFilterButton: true }, minWidth: 200 },
    {
      headerName: "Start $",
      field: "startBalance",
      valueFormatter: (p) => (p.value != null ? p.value.toLocaleString() : ""),
    },
    {
      headerName: "End $",
      field: "endBalance",
      valueFormatter: (p) => (p.value != null ? p.value.toLocaleString() : ""),
    },
    {
      headerName: "Balance Diff",
      field: "delta",
      valueFormatter: (p) => (p.value != null ? p.value.toFixed(2) : ""),
      cellStyle: (p) =>
        p.value != null && Math.abs(p.value) > 0.01 ? { color: "#b02a37", fontWeight: 600 } : null,
    },
    { headerName: "Transactions", field: "txCount", width: 120 },
    {
      headerName: "Status",
      field: "reconciliationStatus",
      cellRenderer: (p) => {
        const v = p.value;
        if (v === "reconciled" || v === "matched")
          return (
            <Badge variant="success">
              <FontAwesomeIcon icon="check" /> Reconciled
            </Badge>
          );
        if (v === "mismatch")
          return (
            <Badge variant="danger">
              <FontAwesomeIcon icon="times" /> Mismatch
            </Badge>
          );
        if (v === "unreconciled") return <Badge variant="warning">Not reconciled</Badge>;
        return <Badge variant="secondary">Unknown</Badge>;
      },
    },
  ];

  return (
    <div className="p-4">
      <Row>
        <Col>
          <h2>Statement coverage</h2>
          <p className="text-muted mb-0">
            Every month, every account — do you have the statement, and does it reconcile?
            Use this view to spot missing periods before closing the engagement and to
            verify every imported statement matches its stated ending balance.
          </p>
        </Col>
      </Row>

      {/* Summary chips */}
      <Row className="mt-3">
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Account-months covered</small>
            <h4 className="mb-0">
              {summary.coveredMonths}{" "}
              <small className="text-muted">/ {summary.totalMonths}</small>
            </h4>
            {summary.missingMonths > 0 && (
              <Badge variant="danger" className="mt-1">
                {summary.missingMonths} missing
              </Badge>
            )}
          </Card>
        </Col>
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Statements reconciled</small>
            <h4 className="mb-0">
              {summary.reconciledStatements}{" "}
              <small className="text-muted">/ {summary.totalStatements}</small>
            </h4>
            {summary.unreconciledStatements > 0 && (
              <Badge variant="warning" className="mt-1">
                {summary.unreconciledStatements} to review
              </Badge>
            )}
          </Card>
        </Col>
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Accounts</small>
            <h4 className="mb-0">{accounts.length}</h4>
            <small className="text-muted">{summary.perAccount.length} tracked</small>
          </Card>
        </Col>
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Engagement span</small>
            <h4 className="mb-0">
              {months.length} <small className="text-muted">months</small>
            </h4>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mt-4">
        <Col md="2">
          <Form.Group>
            <Form.Label>Start date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md="2">
          <Form.Group>
            <Form.Label>End date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md="2" className="d-flex align-items-end">
          <Button variant="outline-secondary" onClick={getGaps}>Recalculate</Button>
        </Col>
      </Row>

      {/* Legend */}
      <Row className="mt-3">
        <Col>
          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            {CELL_LEGEND.map((l) => (
              <div key={l.state} className="d-flex align-items-center">
                <div
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: CELL_COLORS[l.state],
                    border: `1px solid ${CELL_BORDER[l.state]}`,
                    borderRadius: 3,
                    marginRight: 6,
                  }}
                />
                <small>
                  <strong>{l.label}</strong>
                  <span className="text-muted"> — {l.body}</span>
                </small>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      {/* Heatmap */}
      {accounts.length > 0 && months.length > 0 && (
        <Row className="mt-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4 className="mb-0">Coverage grid</h4>
              <Button variant="outline-secondary" size="sm" onClick={exportCoverageCsv}>
                <FontAwesomeIcon icon="download" className="mr-1" />
                Export CSV
              </Button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                className="table table-borderless mb-0"
                style={{ borderCollapse: "separate", borderSpacing: 2 }}
              >
                <thead>
                  <tr>
                    <th style={{ minWidth: 180, position: "sticky", left: 0, background: "white" }}>
                      Account
                    </th>
                    {months.map((m) => (
                      <th
                        key={m}
                        style={{
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          fontWeight: 400,
                          fontSize: 11,
                          padding: "4px 0",
                          minWidth: 24,
                          textAlign: "center",
                        }}
                      >
                        {m}
                      </th>
                    ))}
                    <th className="text-right" style={{ minWidth: 100 }}>
                      Coverage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => {
                    const perAcc = summary.perAccount.find((p) => p.account === a);
                    return (
                      <tr key={a}>
                        <td
                          style={{
                            fontWeight: 500,
                            position: "sticky",
                            left: 0,
                            background: "white",
                          }}
                        >
                          {a}
                        </td>
                        {months.map((m) => {
                          const info = cellInfo(a, m);
                          const tipBody = (
                            <div style={{ textAlign: "left" }}>
                              <div>
                                <strong>{a}</strong> · {m}
                              </div>
                              <div>Status: {stateLabel(info.state)}</div>
                              {info.statements.map((s) => {
                                // A statement lights up every month its
                                // [start,end] span touches, so a statement that
                                // crosses a month boundary shows up in two
                                // adjacent tiles. Clip the shown range to THIS
                                // tile's month so contiguous tiles display
                                // different date ranges (e.g. May tile shows
                                // 05-01→05-31, June tile shows 06-01→06-03)
                                // instead of repeating the identical full
                                // statement period and reading as a duplicate.
                                const sStart = moment(String(s.startDate).slice(0, 10));
                                const sEnd = moment(String(s.endDate).slice(0, 10));
                                const monthStart = moment(m + "-01").startOf("month");
                                const monthEnd = moment(m + "-01").endOf("month");
                                const clipStart = moment.max(sStart, monthStart);
                                const clipEnd = moment.min(sEnd, monthEnd);
                                const spansOtherMonths =
                                  sStart.isBefore(monthStart) || sEnd.isAfter(monthEnd);
                                return (
                                  <div
                                    key={`${s.documentId}-${s.chunk}`}
                                    className="mt-1"
                                  >
                                    <div>
                                      <strong>{docName(s.documentId)}</strong>
                                    </div>
                                    <div>
                                      {clipStart.format("YYYY-MM-DD")} →{" "}
                                      {clipEnd.format("YYYY-MM-DD")}
                                    </div>
                                    {spansOtherMonths && (
                                      <div style={{ opacity: 0.75, fontStyle: "italic" }}>
                                        part of statement{" "}
                                        {sStart.format("YYYY-MM-DD")} →{" "}
                                        {sEnd.format("YYYY-MM-DD")}
                                      </div>
                                    )}
                                    {s.startBalance != null && s.endBalance != null && (
                                      <div>
                                        Bal: ${s.startBalance.toLocaleString()} → $
                                        {s.endBalance.toLocaleString()}
                                      </div>
                                    )}
                                    <div>{s.txCount} transactions</div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                          return (
                            <OverlayTrigger
                              key={m}
                              placement="top"
                              overlay={<BsTooltip id={`tip-${a}-${m}`}>{tipBody}</BsTooltip>}
                            >
                              <td
                                style={{
                                  backgroundColor: CELL_COLORS[info.state],
                                  border: `1px solid ${CELL_BORDER[info.state]}`,
                                  minWidth: 24,
                                  height: 28,
                                  cursor: "default",
                                }}
                              />
                            </OverlayTrigger>
                          );
                        })}
                        <td className="text-right">
                          <small>
                            {perAcc?.coveragePct ?? 0}% covered
                            <br />
                            <span className="text-muted">
                              {perAcc?.reconciledStatements ?? 0}/{perAcc?.statements ?? 0} reconciled
                            </span>
                          </small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Col>
        </Row>
      )}

      {/* Gap callout */}
      {gapMonths.length > 0 && (
        <Row className="mt-3">
          <Col>
            <Card body style={{ borderLeft: "4px solid #dc3545" }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>
                    <FontAwesomeIcon icon="exclamation-triangle" className="text-danger mr-2" />
                    {gapMonths.length} missing account-month
                    {gapMonths.length === 1 ? "" : "s"}
                  </strong>
                  <div className="text-muted small">
                    Upload the missing statements to close the gaps.
                  </div>
                </div>
                <Button variant="primary" size="sm" onClick={goToUpload}>
                  <FontAwesomeIcon icon="upload" className="mr-1" />
                  Upload statement
                </Button>
              </div>
              <div className="mt-2">
                {gapMonths.slice(0, 20).map((g, i) => (
                  <Badge key={i} variant="light" className="mr-2 mb-1" style={{ border: "1px solid #dc3545" }}>
                    {g.account} · {g.month}
                  </Badge>
                ))}
                {gapMonths.length > 20 && (
                  <small className="text-muted">…and {gapMonths.length - 20} more</small>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Statement audit table */}
      {statementRows.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4>Statements</h4>
            <p className="text-muted">
              One row per imported statement period. Sort or filter by status to triage
              what still needs reconciliation.
            </p>
            <AgTable
              hasExport={true}
              columnDefs={statementColumnDefs}
              rowData={statementRows}
              height={400}
              width="100%"
            />
          </Col>
        </Row>
      )}

      {/* Overlapping periods — surfaced from the duplicate-bank-transactions
          detector. Renders directly below the existing date-gaps content so
          a forensic accountant sees both kinds of period-integrity issues
          (gaps and overlaps) in one scan. Hidden in tag-level mode and
          when the endpoint errors. */}
      {(jobId || (l1 && l2)) && !duplicatesErrored && (
        <Row className="mt-4">
          <Col>
            <h4>Overlapping periods (potential duplicates)</h4>
            <p className="text-muted">
              Transactions that appear in two statements covering an
              overlapping date window for the same account. Review and
              remove the stale copy.
            </p>
            {(duplicates?.groups?.length ?? 0) === 0 ? (
              <Card body className="text-center text-muted">
                No overlapping duplicates detected.
              </Card>
            ) : (
              <DuplicatesTable groups={duplicates!.groups} />
            )}
          </Col>
        </Row>
      )}

      {accounts.length === 0 && (
        <Row className="mt-4">
          <Col>
            <Card body className="text-center text-muted">
              No statements or transactions found for this project yet. Upload a bank
              statement to populate this view.
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

// Renders the duplicate groups as a flat audit table — one row per group,
// not one per member, so it scans like the Statements table above. The
// "source documents" column shows N pill-style badges for each member's
// document; the description column truncates at 60 chars with the full
// text in a hover tooltip.
const DuplicatesTable: React.FC<{ groups: DuplicateBankTransactionGroup[] }> = ({
  groups,
}) => {
  const truncate = (s: string, n: number) =>
    s && s.length > n ? s.slice(0, n - 1) + "…" : s || "";
  const confidenceVariant = (v: number): "success" | "warning" | "secondary" =>
    v >= 0.8 ? "success" : v >= 0.5 ? "warning" : "secondary";
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table table-sm table-striped mb-0">
        <thead>
          <tr>
            <th>Date</th>
            <th>Account</th>
            <th className="text-right">Amount</th>
            <th>Description</th>
            <th>Source documents</th>
            <th>Similarity</th>
            <th>Confidence</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const desc = (g.members[0]?.description) || "";
            // Dedup by documentId, keep the first member's originalName for
            // each doc so we render filenames instead of raw ids.
            const docs = (() => {
              const seen = new Map<number, string | null | undefined>();
              for (const m of g.members) {
                if (!seen.has(m.documentId)) {
                  seen.set(m.documentId, m.originalName);
                }
              }
              return Array.from(seen.entries()).map(([id, originalName]) => ({
                id,
                originalName,
              }));
            })();
            return (
              <tr key={g.key}>
                <td>{g.date}</td>
                <td>{g.account}</td>
                <td className="text-right">${g.amount.toLocaleString()}</td>
                <td>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BsTooltip id={`dup-desc-${g.key}`}>{desc}</BsTooltip>
                    }
                  >
                    <span>{truncate(desc, 60)}</span>
                  </OverlayTrigger>
                </td>
                <td>
                  {docs.map(({ id, originalName }) => (
                    <Badge
                      key={String(id)}
                      variant="light"
                      style={{
                        border: "1px solid #d0d7de",
                        marginRight: 4,
                      }}
                      title={originalName ? `doc ${id}` : undefined}
                    >
                      {originalName || `doc ${id}`}
                    </Badge>
                  ))}
                </td>
                <td>{Math.round(g.similarity * 100)}%</td>
                <td>
                  <Badge variant={confidenceVariant(g.confidence)}>
                    {Math.round(g.confidence * 100)}%
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
export default DateGaps;
